import { useEffect, useState } from "react";
import type {
  AlertChannel,
  AlertRule,
  AlertRuleKind,
  CreateAlertRuleInput,
  UpdateAlertRuleInput,
} from "@rua-watch/shared";
import { ApiError } from "../api/client";
import {
  useAlertRules,
  useCreateAlertRule,
  useDeleteAlertRule,
  useRunAlertCycle,
  useTestAlertRule,
  useUpdateAlertRule,
} from "../api/hooks";
import { fromUnix } from "../lib/format";
import { Drawer, DrawerField, drawerControlClass } from "./Drawer";

// Admin section for managing F4 alert rules. Listing + create + edit
// + delete + dry-run test + manual cycle trigger. Layout intentionally
// dense — operators tweak many rules at once during initial bootstrap.

const RULE_KIND_LABELS: Record<AlertRuleKind, string> = {
  pass_rate_drop:"Pass rate drop",
  dns_changed:"DNS configuration changed",
  no_reports_received:"No reports received",
  new_unclassified_source:"New unclassified source",
  volume_spike:"Volume spike",
  legit_source_failing:"Known source failing",
};

const KIND_DEFAULTS: Record<AlertRuleKind, Record<string, unknown>> = {
  pass_rate_drop: { threshold: 0.95, hours: 24, minMessages: 50 },
  dns_changed: { lookbackHours: 24 },
  no_reports_received: { thresholdHours: 72 },
  new_unclassified_source: { volumeThreshold: 100, lookbackHours: 24 },
  volume_spike: { multiplier: 5, lookbackHours: 24, minBaseline: 100 },
  legit_source_failing: { threshold: 0.9, lookbackHours: 24, minMessages: 50 },
};

export function AlertRulesPanel() {
  const rules = useAlertRules();
  const cycle = useRunAlertCycle();
  // null = closed; { rule: null } = create; { rule } = edit.
  const [drawer, setDrawer] = useState<{ rule: AlertRule | null } | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const triggerCycle = async () => {
    setError(null);
    setFeedback(null);
    try {
      const r = await cycle.mutateAsync();
      setFeedback(`Evaluated ${r.rulesEvaluated} rule(s) — ${r.fired} fired, ` +`${r.dedupSkipped} deduped, ${r.delivered} delivered, ${r.deliveryErrors} delivery errors`,
      );
    } catch (err) {
      setError(formatErr(err));
    }
  };

  return (
    <section className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3">
      <div className= "flex items-start justify-between gap-3">
        <div>
          <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Alert rules</h2>
          <p className= "text-xs text-slate-500 dark:text-slate-400">
            Hourly cron evaluates every enabled rule and fires events when the
            threshold is crossed. New rules get a 1h silent window so they
            don't pop on stale state — clear <code>silent_until</code> if you
            want immediate evaluation. Delivery is via the channels configured
            on each rule.
          </p>
        </div>
        <div className= "flex gap-2 shrink-0">
          <button
            onClick={triggerCycle}
            disabled={cycle.isPending}
            className= "rounded border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:bg-slate-200"
          >
            {cycle.isPending ?"Running…" :"Run now"}
          </button>
          <button
            onClick={() => setDrawer({ rule: null })}
            className= "rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
          >
            + New rule
          </button>
        </div>
      </div>

      {feedback ? (
        <div className= "rounded-md border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50 dark:bg-emerald-900/30 p-3 text-sm text-emerald-900 dark:text-emerald-200">
          {feedback}
        </div>
      ) : null}
      {error ? (
        <div className= "rounded-md border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
        {rules.isLoading ? (
          <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : rules.data && rules.data.length === 0 ? (
          <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
            No rules yet. Create one to start receiving alerts.
          </p>
        ) : (
          <table className= "min-w-full text-sm">
            <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
              <tr>
                <th className= "px-4 py-2 font-medium">Name</th>
                <th className= "px-4 py-2 font-medium">Kind</th>
                <th className= "px-4 py-2 font-medium">Domain</th>
                <th className= "px-4 py-2 font-medium">Channels</th>
                <th className= "px-4 py-2 font-medium">Enabled</th>
                <th className= "px-4 py-2 font-medium">Silent until</th>
                <th className= "px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
              {(rules.data ?? []).map((rule) => (
                <RuleRow key={rule.id} rule={rule} onEdit={() => setDrawer({ rule })} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertRuleDrawer state={drawer} onClose={() => setDrawer(null)} />
    </section>
  );
}

// Create/edit an alert rule in the side drawer (app-wide standard).
// `state.rule` null → create; a rule → edit. On edit, `kind` and `domain`
// are immutable (the API's UpdateAlertRuleInput omits them), so they show
// read-only; name/threshold/channels remain editable.
function AlertRuleDrawer({
  state,
  onClose,
}: {
  state: { rule: AlertRule | null } | null;
  onClose: () => void;
}) {
  const create = useCreateAlertRule();
  const update = useUpdateAlertRule();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<AlertRuleKind>("pass_rate_drop");
  const [domain, setDomain] = useState("");
  const [thresholdJson, setThresholdJson] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const editing = state?.rule ?? null;
  const isEdit = !!editing;

  useEffect(() => {
    if (!state) return;
    const r = state.rule;
    setName(r?.name ?? "");
    setKind(r?.kind ?? "pass_rate_drop");
    setDomain(r?.domain ?? "");
    setThresholdJson(
      JSON.stringify(r ? r.threshold : KIND_DEFAULTS["pass_rate_drop"], null, 2),
    );
    const webhook = r?.channels.find((c) => c.type === "webhook");
    setWebhookUrl(webhook && "url" in webhook ? webhook.url : "");
    setError(null);
  }, [state]);

  if (!state) return null;

  const onKindChange = (next: AlertRuleKind) => {
    setKind(next);
    setThresholdJson(JSON.stringify(KIND_DEFAULTS[next], null, 2));
  };

  const submit = async () => {
    setError(null);
    let threshold: Record<string, unknown>;
    try {
      threshold = JSON.parse(thresholdJson) as Record<string, unknown>;
    } catch {
      setError("Threshold isn't valid JSON.");
      return;
    }
    const channels: AlertChannel[] = webhookUrl.trim()
      ? [{ type: "webhook", url: webhookUrl.trim() }]
      : [];
    try {
      if (isEdit) {
        const input: UpdateAlertRuleInput = { name: name.trim(), threshold, channels };
        await update.mutateAsync({ id: editing!.id, input });
      } else {
        const input: CreateAlertRuleInput = {
          name: name.trim(),
          kind,
          domain: domain.trim() || null,
          threshold,
          channels,
        };
        await create.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError(formatErr(err));
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Drawer
      open={!!state}
      onClose={onClose}
      title={isEdit ? "Edit alert rule" : "New alert rule"}
      description={
        isEdit
          ? "Kind and domain are fixed after creation — create a new rule to change them."
          : "Fires when the threshold is crossed; delivered via the configured channel."
      }
      footer={
        <div className= "flex items-center justify-end gap-2">
          {error ? <span className= "mr-auto text-xs text-rose-700 dark:text-rose-400">{error}</span> : null}
          <button
            type= "button"
            onClick={onClose}
            className= "rounded border border-slate-300 dark:border-slate-700 px-3 py-1.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type= "button"
            onClick={submit}
            disabled={pending || !name.trim()}
            className= "rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {pending ? "Saving…" : isEdit ? "Save changes" : "Create rule"}
          </button>
        </div>
      }
    >
      <div className= "space-y-4">
        <DrawerField label= "Name">
          <input
            type= "text"
            value={name}
            placeholder= "Pass rate drop on plusdin.com.br"
            onChange={(e) => setName(e.target.value)}
            className={drawerControlClass}
          />
        </DrawerField>

        <DrawerField label= "Kind">
          {isEdit ? (
            <div className= "rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-sm text-slate-700 dark:text-slate-300">
              {RULE_KIND_LABELS[kind]}
            </div>
          ) : (
            <select value={kind} onChange={(e) => onKindChange(e.target.value as AlertRuleKind)} className={drawerControlClass}>
              {(Object.keys(RULE_KIND_LABELS) as AlertRuleKind[]).map((k) => (
                <option key={k} value={k}>{RULE_KIND_LABELS[k]}</option>
              ))}
            </select>
          )}
        </DrawerField>

        <DrawerField label= "Domain" hint={isEdit ? undefined : "Leave blank to apply to all monitored domains."}>
          {isEdit ? (
            <div className= "rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 font-mono text-sm text-slate-700 dark:text-slate-300">
              {domain || "all domains"}
            </div>
          ) : (
            <input
              type= "text"
              value={domain}
              placeholder= "leave blank for all"
              onChange={(e) => setDomain(e.target.value)}
              className={drawerControlClass}
            />
          )}
        </DrawerField>

        <DrawerField label= "Threshold (JSON)" hint="Defaults pre-filled per kind. Edit values; keep keys as-is.">
          <textarea
            value={thresholdJson}
            onChange={(e) => setThresholdJson(e.target.value)}
            rows={6}
            className={`${drawerControlClass} font-mono text-xs`}
          />
        </DrawerField>

        <DrawerField label= "Webhook URL" hint="Slack / MS Teams Incoming Webhook. Leave blank for no channel.">
          <input
            type= "url"
            value={webhookUrl}
            placeholder= "https://hooks.slack.com/..."
            onChange={(e) => setWebhookUrl(e.target.value)}
            className={drawerControlClass}
          />
        </DrawerField>
      </div>
    </Drawer>
  );
}

function RuleRow({ rule, onEdit }: { rule: AlertRule; onEdit: () => void }) {
  const update = useUpdateAlertRule();
  const remove = useDeleteAlertRule();
  const test = useTestAlertRule();
  const [testResult, setTestResult] = useState<string | null>(null);

  const toggleEnabled = () =>
    update.mutate({ id: rule.id, input: { enabled: !rule.enabled } });
  const clearSilence = () => update.mutate({ id: rule.id, input: { silentUntil: null } });
  const handleDelete = () => {
    if (!confirm(`Delete alert rule"${rule.name}"?`)) return;
    remove.mutate(rule.id);
  };
  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await test.mutateAsync(rule.id);
      setTestResult(
        result.candidates.length === 0
          ?"No candidates would fire right now."
          :`Would fire ${result.candidates.length} event(s):\n` +
            result.candidates.map((c) => `• ${c.summary}`).join("\n"),
      );
    } catch (err) {
      setTestResult(formatErr(err));
    }
  };

  return (
    <>
      <tr>
        <td className= "px-4 py-2">{rule.name}</td>
        <td className= "px-4 py-2">
          <span className= "rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 dark:text-slate-400">
            {rule.kind}
          </span>
        </td>
        <td className= "px-4 py-2 font-mono text-xs text-slate-600 dark:text-slate-400">
          {rule.domain ?? <span className= "text-slate-400 dark:text-slate-500">all</span>}
        </td>
        <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
          {rule.channels.length === 0
            ? <span className= "text-amber-700 dark:text-amber-300">no channels</span>
            : rule.channels.map((c) => c.type).join(", ")}
        </td>
        <td className= "px-4 py-2">
          <button
            onClick={toggleEnabled}
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              rule.enabled
                ?"bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-200"
                :"bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200"
            }`}
          >
            {rule.enabled ?"enabled" :"disabled"}
          </button>
        </td>
        <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
          {rule.silentUntil && rule.silentUntil > Math.floor(Date.now() / 1000) ? (
            <button
              onClick={clearSilence}
              title={`Silent until ${fromUnix(rule.silentUntil)} — click to clear`}
              className= "text-amber-700 dark:text-amber-300 hover:underline"
            >
              {fromUnix(rule.silentUntil)}
            </button>
          ) : (
            <span className= "text-slate-400 dark:text-slate-500">—</span>
          )}
        </td>
        <td className= "px-4 py-2 text-right">
          <div className= "inline-flex gap-1">
            <button
              onClick={onEdit}
              className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Edit
            </button>
            <button
              onClick={handleTest}
              disabled={test.isPending}
              className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Test
            </button>
            <button
              onClick={handleDelete}
              disabled={remove.isPending}
              className= "rounded border border-red-300 dark:border-red-900/40 px-2 py-1 text-xs text-red-700 dark:text-red-300 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {testResult ? (
        <tr>
          <td colSpan={7} className= "bg-slate-50 dark:bg-slate-800/60 px-4 py-2">
            <pre className= "whitespace-pre-wrap text-xs text-slate-700 dark:text-slate-300">{testResult}</pre>
            <button
              onClick={() => setTestResult(null)}
              className= "mt-1 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            >
              dismiss
            </button>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; hint?: string } | undefined;
    return body?.hint ?`${body.error ?? err.message} — ${body.hint}` : (body?.error ?? err.message);
  }
  return (err as Error).message ?? "Unknown error";
}
